import React, { useId, useMemo, useState } from "react";
import type { FormattedInvoicePayload } from "../../../formattedInvoice/formattedInvoiceData";
import { CONDITION_LIMITS, validateConditionalDefinition, type ConditionalFormattingRule, type ConditionalPredicate, type ConditionalStyleOverrides } from "../../nativeConditions.mjs";
import {
  CONDITIONAL_STYLE_LABELS, conditionalFieldOptions, conditionalPredicateErrors, conditionalRules,
  conditionalSummary, conditionalTargets, defaultConditionalPredicate,
  type ConditionalDataset, type ConditionalRuleAction, type ConditionalTarget,
} from "../conditionalAuthoring";
import { asRecord, cloneValue, type DesignerSelection } from "../reportDesignerModel";
import ConditionalPredicateEditor from "./ConditionalPredicateEditor";
import pageStyles from "./ReportDesignerPage.module.css";
import styles from "./ConditionalRulesInspector.module.css";

type Draft = { id?: string; category: "visibility" | "formatting"; when: ConditionalPredicate; style: ConditionalStyleOverrides };

export default function ConditionalRulesInspector({ payload, selection, datasets, onCommand }: {
  payload: FormattedInvoicePayload; selection: DesignerSelection; datasets: ConditionalDataset[];
  onCommand: (targetKey: string, action: ConditionalRuleAction) => void;
}) {
  const targets = useMemo(() => conditionalTargets(asRecord(payload.layout), selection), [payload, selection]);
  const [targetKey, setTargetKey] = useState(targets[0]?.key || "");
  const target = targets.find(candidate => candidate.key === targetKey) || targets[0];
  const selectionKey = JSON.stringify(selection);
  return (
    <details className={pageStyles["designer-property-section"]} open>
      <summary>Conditional Rules</summary>
      <div className={styles["conditional-section"]}>
        <small>Internal authoring only. Rules cannot be saved, copied or executed through the API.</small>
        {!target ? <p role="status">Select a supported single native element, band, column or table region. This selection does not support conditional authoring.</p> : (
          <>
            {targets.length > 1 && <label>Rule target<select aria-label="Rule target" value={target.key} onChange={event => setTargetKey(event.target.value)}>{targets.map(candidate => <option value={candidate.key} key={candidate.key}>{candidate.label}</option>)}</select></label>}
            <TargetRules key={`${selectionKey}:${target.key}`} payload={payload} target={target} datasets={datasets} onCommand={onCommand} />
          </>
        )}
      </div>
    </details>
  );
}

function TargetRules({ payload, target, datasets, onCommand }: {
  payload: FormattedInvoicePayload; target: ConditionalTarget; datasets: ConditionalDataset[];
  onCommand: (key: string, action: ConditionalRuleAction) => void;
}) {
  const fields = useMemo(() => conditionalFieldOptions(target, datasets), [target, datasets]);
  const rules = conditionalRules(target.node);
  const formatting = rules.formatting || [];
  const [draft, setDraft] = useState<Draft | null>(null);
  const [commandError, setCommandError] = useState("");
  const validation = useMemo(() => validateConditionalDefinition(payload), [payload]);
  const errors = draft ? conditionalPredicateErrors(draft.when, target, fields) : [];
  if (draft?.category === "formatting") {
    if (!Object.keys(draft.style).length) errors.push("Select at least one style override.");
    for (const [key, value] of Object.entries(draft.style)) {
      if (["color", "backgroundColor", "borderColor"].includes(key) && (typeof value !== "string" || !/^#[0-9a-f]{6}$/i.test(value))) errors.push("Colors must use opaque #RRGGBB values.");
      if (key === "borderWidth" && (typeof value !== "number" || !Number.isFinite(value) || value < 0 || value > 3)) errors.push("Border width must be between 0 and 3 points.");
    }
  }
  function act(action: ConditionalRuleAction) {
    setCommandError("");
    try { onCommand(target.key, action); return true; }
    catch (error) { setCommandError(error instanceof Error ? error.message : "The rule could not be changed."); return false; }
  }
  function add(category: Draft["category"]) {
    setCommandError("");
    setDraft({ category, when: defaultConditionalPredicate(fields), style: {} });
  }
  function save() {
    if (!draft || errors.length) return;
    const action: ConditionalRuleAction = draft.id ? { type: "update", id: draft.id, when: draft.when, ...(draft.category === "formatting" ? { style: draft.style } : {}) } :
      draft.category === "visibility" ? { type: "addVisibility", when: draft.when } : { type: "addFormatting", when: draft.when, style: draft.style };
    if (act(action)) setDraft(null);
  }
  function row(rule: NonNullable<typeof rules.visibleWhen> | ConditionalFormattingRule, category: Draft["category"], index = 0) {
    const ruleErrors = [...conditionalPredicateErrors(rule.when, target, fields), ...validation.errors.filter(error => error.ruleId === rule.id).map(error => error.message)];
    return (
      <div className={styles["conditional-rule"]} data-testid={`conditional-rule-${rule.id}`} key={rule.id}>
        <strong>{category === "visibility" ? "Show when" : `${index + 1}. Format when`} {conditionalSummary(rule.when, fields)}</strong>
        <small>{rule.enabled === false ? "Disabled" : "Enabled"} / {ruleErrors.length ? "Needs attention" : "Valid"}</small>
        {"style" in rule && <small>{Object.entries(rule.style).map(([key, value]) => `${key}: ${String(value)}`).join(", ")}</small>}
        <div className={styles["conditional-actions"]}>
          <button type="button" onClick={() => setDraft({ id: rule.id, category, when: cloneValue(rule.when), style: "style" in rule ? cloneValue(rule.style) : {} })}>Edit {category === "visibility" ? "visibility" : `format ${index + 1}`}</button>
          <button aria-label={`${rule.enabled === false ? "Enable" : "Disable"} rule ${rule.id}`} type="button" onClick={() => act({ type: "update", id: rule.id, enabled: rule.enabled === false })}>{rule.enabled === false ? "Enable" : "Disable"}</button>
          {category === "formatting" && <>
            <button aria-label={`Duplicate rule ${rule.id}`} disabled={formatting.length >= CONDITION_LIMITS.rulesPerTarget} type="button" onClick={() => act({ type: "duplicate", id: rule.id })}>Duplicate</button>
            <button aria-label={`Move rule ${rule.id} up`} disabled={index === 0} type="button" onClick={() => act({ type: "move", id: rule.id, direction: -1 })}>Up</button>
            <button aria-label={`Move rule ${rule.id} down`} disabled={index === formatting.length - 1} type="button" onClick={() => act({ type: "move", id: rule.id, direction: 1 })}>Down</button>
          </>}
          <button aria-label={`Delete rule ${rule.id}`} type="button" onClick={() => { if (act({ type: "remove", id: rule.id }) && draft?.id === rule.id) setDraft(null); }}>Delete</button>
        </div>
        {ruleErrors.length > 0 && <p role="alert">{ruleErrors.join(" ")}</p>}
      </div>
    );
  }
  return (
    <>
      <small>Target: {target.label}. Scope: {target.scopes.join(", ")}.</small>
      <section aria-label="Visibility rules" className={styles["conditional-category"]}>
        <h4>Visibility</h4>
        <small>Presentation only. Static hiding remains authoritative; totals never change.</small>
        {rules.visibleWhen ? row(rules.visibleWhen, "visibility") : <button type="button" onClick={() => add("visibility")}>Add visibility rule</button>}
      </section>
      <section aria-label="Formatting rules" className={styles["conditional-category"]}>
        <h4>Formatting</h4>
        {!target.styleKeys.length ? <small>This target supports visibility only. Select a detail cell or supported region for formatting.</small> : <>
          {formatting.map((rule, index) => row(rule, "formatting", index))}
          <button disabled={formatting.length >= CONDITION_LIMITS.rulesPerTarget} type="button" onClick={() => add("formatting")}>Add formatting rule</button>
          {formatting.length > 1 && <small>Later matching rules override overlapping properties only. Row/group formatting precedes cell formatting.</small>}
        </>}
      </section>
      {draft && <section aria-label="Conditional rule editor" className={styles["conditional-draft"]}>
        <h4>{draft.id ? "Edit" : "New"} {draft.category} rule</h4>
        <ConditionalPredicateEditor value={draft.when} fields={fields} onChange={when => setDraft({ ...draft, when })} />
        {draft.category === "formatting" && <StyleOverrides value={draft.style} target={target} onChange={style => setDraft({ ...draft, style })} />}
        <small>{conditionalSummary(draft.when, fields)}</small>
        {errors.length > 0 && <p role="alert" aria-live="polite">{[...new Set(errors)].join(" ")}</p>}
        <div className={styles["conditional-actions"]}><button disabled={errors.length > 0} type="button" onClick={save}>Apply rule</button><button type="button" onClick={() => setDraft(null)}>Cancel rule edit</button></div>
        <small>Unapplied edits do not affect Preview. Delete recovery is available with Undo.</small>
      </section>}
      {commandError && <p role="alert">{commandError}</p>}
    </>
  );
}

function StyleOverrides({ value, target, onChange }: { value: ConditionalStyleOverrides; target: ConditionalTarget; onChange: (value: ConditionalStyleOverrides) => void }) {
  const id = useId().replace(/:/g, "");
  function change(key: keyof ConditionalStyleOverrides, next: string | boolean | number | undefined) {
    const style = { ...value };
    if (next === undefined) delete style[key];
    else if (key === "bold" || key === "italic") style[key] = next === true;
    else if (key === "borderWidth") style[key] = typeof next === "number" ? next : NaN;
    else style[key] = String(next);
    onChange(style);
  }
  return <fieldset className={styles["conditional-predicate"]}><legend>Style overrides</legend>
    {target.styleKeys.map(key => <div data-testid={`conditional-style-${key}`} key={key}>
      <label htmlFor={`conditional${id}${key}Override`}>Override {CONDITIONAL_STYLE_LABELS[key].toLowerCase()}</label>
      <input id={`conditional${id}${key}Override`} type="checkbox" checked={value[key] !== undefined} onChange={event => change(key, !event.target.checked ? undefined : key === "bold" || key === "italic" ? true : key === "borderWidth" ? 1 : "#23324a")} />
      {value[key] !== undefined && <>
        <label htmlFor={`conditional${id}${key}Value`}>{CONDITIONAL_STYLE_LABELS[key]}</label>
        {key === "bold" || key === "italic" ? <select id={`conditional${id}${key}Value`} value={String(value[key])} onChange={event => change(key, event.target.value === "true")}><option value="true">On</option><option value="false">Off</option></select> :
          <input id={`conditional${id}${key}Value`} type={key === "borderWidth" ? "number" : "text"} min={key === "borderWidth" ? 0 : undefined} max={key === "borderWidth" ? 3 : undefined} step={key === "borderWidth" ? 0.25 : undefined} value={typeof value[key] === "number" && !Number.isFinite(value[key]) ? "" : String(value[key])} onChange={event => change(key, key === "borderWidth" ? event.target.value === "" ? NaN : Number(event.target.value) : event.target.value)} />}
      </>}
    </div>)}
  </fieldset>;
}
